<template>
  <main class="news-view">
    <div class="news-shell">
      <header class="page-header">
        <span class="eyebrow">LATEST NOTICES</span>
        <h1>{{ pageTitle }}</h1>
      </header>

      <section class="notice-list" :aria-label="pageTitle">
        <article v-for="notice in notices" :key="notice.id" class="notice-item">
          <div class="notice-heading">
            <div>
              <p class="notice-date">{{ notice.date }}</p>
              <h2>{{ notice.title }}</h2>
              <span class="title-mark" aria-hidden="true"></span>
            </div>
          </div>

          <div :id="`${notice.id}-detail`" class="notice-detail">
            <p
              v-for="paragraph in notice.intro"
              :key="paragraph"
              class="intro-paragraph"
            >
              {{ paragraph }}
            </p>

            <h3
              class="document-card-title"
              :class="{ 'without-intro': notice.intro.length === 0 }"
            >
              {{ notice.detailTitle }}
            </h3>

            <section
              v-for="section in notice.sections"
              :key="section.heading"
              class="detail-section"
            >
              <h3>{{ section.heading }}</h3>
              <div class="detail-content">
                <p v-for="paragraph in section.paragraphs" :key="paragraph">
                  {{ paragraph }}
                </p>
                <dl v-if="section.fields" class="detail-fields">
                  <div
                    v-for="field in section.fields"
                    :key="field.label"
                    class="detail-field"
                  >
                    <dt>{{ field.label }}</dt>
                    <dd>{{ field.value }}</dd>
                  </div>
                </dl>
                <ul v-if="section.items" class="detail-list">
                  <li v-for="item in section.items" :key="item">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </section>

            <div v-if="notice.closing.length" class="notice-closing">
              <p v-for="line in notice.closing" :key="line">{{ line }}</p>
            </div>
          </div>

          <p v-if="notice.attachment" class="attachment-label">
            {{ isZh ? "附件" : "Attachment" }}
          </p>

          <a
            v-if="notice.attachment"
            class="pdf-attachment"
            :href="notice.attachment.url"
            target="_blank"
            rel="noopener"
          >
            <span class="pdf-type" aria-hidden="true">PDF</span>
            <span class="pdf-copy">
              <strong>{{ notice.attachment.name }}</strong>
              <small>{{ isZh ? "点击查看 PDF 文件" : "Open PDF file" }}</small>
            </span>
            <span class="pdf-arrow" aria-hidden="true">↗</span>
          </a>
        </article>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, onActivated, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

interface Field {
  label: string;
  value: string;
}

interface NoticeSection {
  heading: string;
  paragraphs?: string[];
  fields?: Field[];
  items?: string[];
}

interface NoticeAttachment {
  name: string;
  url: string;
}

interface Notice {
  id: string;
  date: string;
  title: string;
  detailTitle: string;
  intro: string[];
  sections: NoticeSection[];
  closing: string[];
  attachment?: NoticeAttachment;
}

const { locale } = useI18n();
const route = useRoute();
const isZh = computed(() => locale.value === "zh");

const allNotices: Notice[] = [
  {
    id: "selection-notice",
    date: "2024.07.15",
    title: "三水数据中心机房运维服务项目·比选公告",
    detailTitle: "项目公开比选基本要素卡",
    intro: [],
    sections: [
      {
        heading: "项目要点",
        fields: [
          { label: "项目名称", value: "【三水数据中心机房运维服务项目】" },
          { label: "项目编号", value: "【BX-2024-0031】" },
          { label: "预算控制价", value: "【56】万元" },
          { label: "服务期限", value: "自合同签订之日起【12个月】" },
          {
            label: "服务核心",
            value: "网络/服务器/终端系统的日常驻场巡检、故障响应及技术支持",
          },
        ],
      },
      {
        heading: "参选资格",
        items: [
          "具备独立法人资质（提供营业执照复印件）；",
          "具有近3年同类IT驻场服务业绩及满意度证明；",
          "本项目不接受联合体参选。",
        ],
      },
      {
        heading: "时间与递交",
        fields: [
          {
            label: "报名及截止时间",
            value: "【2024年07月15日】至【2024年07月20日 17:00】",
          },
          { label: "材料递交邮箱", value: "【bxq0047@gmail.com】" },
        ],
      },
      {
        heading: "采购联系人",
        fields: [
          { label: "联系人", value: "【张经理】" },
          { label: "联系电话", value: "【13822393029】" },
        ],
      },
    ],
    closing: [],
  },
  {
    id: "award-announcement",
    date: "2024.08.01",
    title: "IT驻场运维服务项目·成交公示",
    detailTitle: "成交结果确认卡",
    intro: [
      "项目名称：【三水数据中心机房运维服务项目】（编号：BX-2024-0031）",
      "评审日期：【2024年07月28日】",
      "经比选小组综合评审，本项目成交结果如下：",
    ],
    sections: [
      {
        heading: "成交结果",
        fields: [
          { label: "成交供应商", value: "环迅通科技（北京）有限公司" },
          { label: "成交金额", value: "【54】万元" },
          { label: "服务周期", value: "【1年】" },
        ],
      },
      {
        heading: "异议反馈",
        paragraphs: [
          "本公示期为【3个工作日】（自【2024年08月01日】起）。如有异议，请在公示期内向采购人提交书面反馈。",
          "邮箱：【bxq0047@gmail.com】",
        ],
      },
    ],
    closing: [],
    attachment: {
      name: "成交结果通知书.pdf",
      url: "/documents/成交结果通知书.pdf",
    },
  },
];

const isAwardPage = computed(() => route.path === "/news/award");
const notices = computed(() =>
  allNotices.filter((notice) =>
    isAwardPage.value
      ? notice.id === "award-announcement"
      : notice.id === "selection-notice"
  )
);
const pageTitle = computed(() => {
  if (isAwardPage.value) {
    return isZh.value ? "成交公示" : "Award Notice";
  }
  return isZh.value ? "比选公告" : "Selection Notice";
});

onActivated(() => window.scrollTo({ top: 0 }));
watch(
  () => route.path,
  () => window.scrollTo({ top: 0 })
);
</script>

<style scoped lang="less">
.news-view {
  min-height: 100vh;
  padding: 8.5rem 2rem 6rem;
  background: radial-gradient(
      circle at 94% 5%,
      rgba(114, 205, 251, 0.12),
      transparent 26rem
    ),
    #f7f8fa;
  color: #20232a;
}

.news-shell {
  width: 100%;
  max-width: var(--view-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: 3rem;

  .eyebrow {
    display: block;
    margin-bottom: 0.75rem;
    color: #0a66b7;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.16em;
  }

  h1 {
    margin: 0;
    font-family: "HarmonyOS_Bold", "HarmonyOS_Regular", sans-serif;
    font-size: clamp(2.4rem, 4vw, 4.2rem);
    line-height: 1.12;
  }

  p {
    margin: 1rem 0 0;
    color: #6b7280;
    font-size: 1.05rem;
  }
}

.notice-list {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e7e9ee;
  box-shadow: 0 20px 55px rgba(24, 47, 78, 0.07);
}

.notice-item {
  padding: 3rem 3.5rem;
  border-bottom: 1px solid #e4e7ec;

  &:last-child {
    border-bottom: 0;
  }
}

.notice-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;

  h2 {
    margin: 0;
    font-family: "HarmonyOS_Bold", "HarmonyOS_Regular", sans-serif;
    font-size: clamp(1.5rem, 2.4vw, 2rem);
    line-height: 1.35;
  }
}

.notice-date {
  margin: 0 0 0.55rem;
  color: #6b7280;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
}

.title-mark {
  display: block;
  width: 3.15rem;
  height: 3px;
  margin-top: 1rem;
  background: #0a66b7;
}

.pdf-attachment {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  padding: 1rem 1.2rem;
  border: 1px solid #dbe2ea;
  background: #f7f9fb;
  color: #252b33;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: #0a66b7;
    background: #f1f7fc;
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid rgba(10, 102, 183, 0.24);
    outline-offset: 3px;
  }
}

.attachment-label {
  margin: 2.25rem 0 0;
  color: #24445f;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.pdf-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  flex: 0 0 auto;
  background: #244f74;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.pdf-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;

  strong {
    overflow-wrap: anywhere;
    font-size: 1rem;
  }

  small {
    color: #6b7280;
    font-size: 0.82rem;
  }
}

.pdf-arrow {
  color: #0a66b7;
  font-size: 1.5rem;
  line-height: 1;
}

.notice-detail {
  margin-top: 2.25rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e8ed;
}

.intro-paragraph {
  margin: 0 0 0.9rem;
  color: #434a54;
  font-size: 1rem;
  line-height: 1.85;
}

.document-card-title {
  margin: 1.5rem 0 0;
  padding: 0.9rem 1.25rem;
  background: #244f74;
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
  text-align: center;

  &.without-intro {
    margin-top: 0;
  }
}

.detail-section {
  display: grid;
  grid-template-columns: 10.5rem minmax(0, 1fr);
  margin-top: 1.25rem;
  border: 1px solid #e1e6ec;

  h3 {
    margin: 0;
    padding: 1.25rem 1.4rem;
    background: #f0f4f8;
    color: #24445f;
    font-size: 1rem;
    line-height: 1.6;
  }
}

.detail-content {
  padding: 1.2rem 1.5rem;
  color: #424953;
  line-height: 1.8;

  > p {
    margin: 0 0 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.detail-fields {
  display: grid;
  gap: 0.55rem;
  margin: 0;
}

.detail-field {
  display: grid;
  grid-template-columns: 9rem minmax(0, 1fr);
  gap: 1rem;

  dt,
  dd {
    margin: 0;
  }

  dt {
    color: #6a7280;
  }
}

.detail-list {
  display: grid;
  gap: 0.55rem;
  margin: 0;
  padding-left: 1.25rem;

  li::marker {
    color: #0a66b7;
  }
}

.notice-closing {
  margin-top: 1.5rem;
  color: #555d68;
  text-align: right;
  line-height: 1.8;

  p {
    margin: 0;
  }
}

@media (max-width: 760px) {
  .news-view {
    padding: 7rem 1.25rem 4rem;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .notice-item {
    padding: 2.4rem 1.6rem;
  }

  .detail-field {
    grid-template-columns: 1fr;
    gap: 0.1rem;
  }

  .detail-field dt {
    font-size: 0.88rem;
  }

  .detail-section {
    grid-template-columns: 1fr;

    h3 {
      padding: 0.85rem 1rem;
    }
  }

  .detail-content {
    padding: 1rem;
  }

  .pdf-attachment {
    align-items: flex-start;
    padding: 0.9rem;
  }

}
</style>
